import { RootState, AppThunk } from "ducks/state";
import { createSelector } from "reselect";
import { newNotification } from "./notification";
import { hen, Hen } from "@udok/lib/internal/store";
import { Document } from "@udok/lib/api/models";
import {
  createDocumentTemplate,
  fetchDocumentTemplate,
  fetchDocumentTemplates,
  updateDocumentTemplate,
  deleteDocumentTemplate,
} from "@udok/lib/api/document";
import { getToken, UNAUTHORIZED } from "./auth";
import moment from "moment";
import "moment/locale/pt-br";
moment.locale("pt-br");

export type InitialState = {
  templateByID: { [doteID: string]: Document };
};

// Reducers
const initialState: InitialState = {
  templateByID: {},
};

class AppointmentFormSlice extends Hen<InitialState> {
  templatesLoaded(v: Document[]) {
    v.forEach((s) => {
      this.state.templateByID[String(s.doteID)] = s;
    });
  }

  templateLoaded(v: Document) {
    this.state.templateByID[String(v.doteID)] = v;
  }

  doctorRemoved(v: Document) {
    delete this.state.templateByID[String(v.doteID)];
  }
}

export const [Reducer, actions] = hen(new AppointmentFormSlice(initialState), {
  [UNAUTHORIZED]: () => {
    return initialState;
  },
});

// Selectors
const mainSelector = (state: RootState) => state.appointmentForms;

export const templateListView = createSelector(mainSelector, (state) => {
  return {
    list: Object.keys(state.templateByID)
      .map((doctID) => state.templateByID[doctID])
      .sort(function (a, b) {
        return moment(b.createdAt).diff(moment(a.createdAt));
      }),
  };
});

export const templateView = (state: RootState, props: { doteID: string }) =>
  createSelector(mainSelector, (state) => {
    return {
      template: state.templateByID[props.doteID],
    };
  });

export const getListDocumentsNoFields = (
  state: RootState,
  props: { doteIDList?: string[] }
) =>
  createSelector(mainSelector, (state) => {
    return {
      templates: (props?.doteIDList ?? []).map(
        (id) => state.templateByID[id] ?? {}
      ),
    };
  });

//Actions
export function loadAllClinicTemplates(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return fetchDocumentTemplates(apiToken)
      .then((r) => {
        dispatch(actions.templatesLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function loadOneDocumentTemplate(
  doteID: string
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return fetchDocumentTemplate(apiToken, doteID)
      .then((r) => {
        dispatch(actions.templateLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function createTemplate(document: Document): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return createDocumentTemplate(apiToken, document)
      .then((r) => {
        dispatch(actions.templateLoaded(r));
        dispatch(
          newNotification("general", {
            status: "success",
            message: "Realizado com sucesso!",
          })
        );
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function updateTemplate(document: Document): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return updateDocumentTemplate(apiToken, document)
      .then((r) => {
        dispatch(actions.templateLoaded(r));
        dispatch(
          newNotification("general", {
            status: "success",
            message: "Realizado com sucesso!",
          })
        );
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function removeTemplate(doteID: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return deleteDocumentTemplate(apiToken, doteID)
      .then((r) => {
        dispatch(actions.doctorRemoved(r));
        dispatch(
          newNotification("general", {
            status: "success",
            message: "Realizado com sucesso!",
          })
        );
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}
